import { IDataStore } from "csi/typings";
import { arrayMove } from "csi/utility/arrayUtility";
import { action, makeObservable, observable, runInAction } from "mobx";

class ArrayDataStore<T> implements IDataStore<T> {
	/** array with results */
	@observable.shallow protected _data: T[];

	/** function called to resolve data if not given in constructor and returning length */
	protected readonly _loadCallback?: () => Promise<T[]>;

	/** identity function - if idProperty not provided, then index is used */
	readonly getIdentity?: (item: T) => string;

	/** total number of items in store */
	get total(): number | undefined {
		return this._data?.length;
	}

	/** return item at given index */
	get = (index: number): T => this._data?.[index];

	/** return index of item with given identity value */
	getIdentityIndex = (id: string): number | undefined => {
		const { _data, getIdentity } = this;

		if (!_data) {
			return;
		}

		if (getIdentity) {
			return _data.findIndex((item: T) => getIdentity(item) === id);
		}

		return Number(id);
	};

	/** return identity of next (or previous) item in the store relative to item with given identity value */
	getNextIdentity = (id: string, previous?: boolean, defaultToFirst?: boolean): string | undefined => {
		const { get, getIdentity, getIdentityIndex, total } = this;
		let index = getIdentityIndex(id);

		if ((index >= 0) && (index < total)) {
			index += (previous ? -1 : 1);
		}

		if ((index === undefined) || (index < 0) || (index === total)) {
			if (defaultToFirst) {
				index = 0;
			} else {
				return undefined;
			}
		}

		const item = get(index);

		return getIdentity?.(item) ?? String(index);
	};

	/** nothing to load - just return total */
	load = async (start?: number, end?: number): Promise<number> => {
		const { _data, _loadCallback } = this;

		if (_loadCallback && !_data) {
			const data = await _loadCallback();

			runInAction(() => {
				this._data = data;
			});
		}

		return this._data.length;
	};

	/** move item from one index to another */
	@action move = (from: number, to: number): void => {
		arrayMove(this._data, from, to);
	};

	/** remove item from store */
	@action remove = (id: string): void => {
		const { getIdentityIndex, _data } = this;
		const index = getIdentityIndex(id);

		if (index >= 0) {
			_data.splice(index, 1);
		}
	};

	constructor(a: T[] | null, idProperty?: string, loadCallback?: () => Promise<T[]>) {
		makeObservable(this);
		this._data = a;
		this._loadCallback = loadCallback;

		if (idProperty) {
			this.getIdentity = (item: T): string => item[idProperty];
		}
	}
}

export default ArrayDataStore;